// Data hook for the Season Dashboard — the fetch + rollup layer ported from
// StatsView (games fetch, season filter, buildSeasonStats, team pitching
// aggregates, streak signals) plus the new insight engines.

import { useState, useEffect, useMemo } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { gamesForSeason } from '../../lib/utils';
import { buildSeasonStats } from '../../lib/season-stats';
import { fieldTimeSourceOf } from '../../lib/gc-innings';
import { calculateFIP, calculateBABIP, calculateDSS } from '../../lib/dss-engine';
import { computeStreakSignals, groupSignalsByPlayer } from '../../lib/streak-signals';
import { computeWinningFormula } from '../../lib/winning-formula';
import { computeHitterTruth } from '../../lib/hitter-truth';
import { selfInflictedRuns } from '../../lib/pitching-plan';

export function useSeasonStats(team: any, players: any[], viewSeason?: { season: string; year: number } | null) {
  const [allGames, setAllGames] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const isHistoryMode = !!viewSeason;

  useEffect(() => {
    const fetchGames = async () => {
      try {
        const q = query(collection(db, `teams/${team.id}/games`));
        const snapshot = await getDocs(q);
        setAllGames(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Failed to fetch games', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGames();
  }, [team.id]);

  const games = useMemo(() => {
    if (viewSeason) return gamesForSeason(allGames, viewSeason.season, viewSeason.year, false);
    if (team?.season && team?.seasonYear != null) {
      return gamesForSeason(allGames, team.season, team.seasonYear, true);
    }
    return allGames;
  }, [allGames, viewSeason, team?.season, team?.seasonYear]);

  return useMemo(() => {
    const { stats, lastGCOuting, totalGamesWithLogs } = buildSeasonStats(players, games, { isHistoryMode, fieldTimeSource: fieldTimeSourceOf(team), defaultInnings: team?.defaultInnings });

    // Team-level pitching aggregates (ported verbatim from StatsView)
    let tIP = 0, tK = 0, tBB = 0, tHBP = 0, tHR = 0, tH = 0, tBF = 0, tER = 0;
    Object.values(stats).forEach((s: any) => {
      if (s.pitching.ip > 0) {
        tIP += s.pitching.ip; tK += s.pitching.so; tBB += s.pitching.bb;
        tHBP += s.pitching.hbp; tHR += s.pitching.hr; tH += s.pitching.h;
        tBF += s.pitching.bf; tER += s.pitching.er;
      }
    });
    const teamERA = tIP > 0 ? (tER / tIP) * 9 : null;
    const teamFIP = tIP > 0 ? calculateFIP(tK, tBB, tHBP, tHR, tIP) : null;
    const teamBABIP = tBF > 0 ? calculateBABIP(tH, tHR, tBF, tBB, tHBP, tK) : null;
    const teamDSSResult = teamERA != null && teamFIP != null
      ? calculateDSS(teamERA, teamFIP, teamBABIP, tIP)
      : null;

    const sortedGames = [...games]
      .filter(g => g.status === 'completed')
      .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime());

    const streakSignals = computeStreakSignals(players, sortedGames, stats);
    const signalsByPlayer = groupSignalsByPlayer(streakSignals);

    // Season record + runs from recorded results
    const resultGames = games.filter(g => g.status === 'completed' && g.result);
    const record = {
      w: resultGames.filter(g => g.result.outcome === 'W').length,
      l: resultGames.filter(g => g.result.outcome === 'L').length,
      t: resultGames.filter(g => g.result.outcome === 'T').length,
      runsFor: resultGames.reduce((s, g) => s + (g.result.myScore ?? 0), 0),
      runsAgainst: resultGames.reduce((s, g) => s + (g.result.opponentScore ?? 0), 0),
      last5: [...resultGames]
        .sort((a, b) => new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime())
        .slice(-5)
        .map(g => g.result.outcome as string),
    };

    // Team story-tile aggregates from season rollups (undefined-safe: null = "—")
    const team_ = (() => {
      let pa = 0, ab = 0, so = 0, kLooking: number | null = null;
      let qab: number | null = null, ps: number | null = null;
      let bb = 0, hbp: number | null = null, sb = 0;
      let bbAllowed = 0, hbpAllowed = 0, sbAllowed = 0, csAllowed = 0;
      let sumOps = 0, wOps = 0, sumAvg = 0, wAvg = 0;
      let contactSum = 0, contactW = 0, riskSum = 0, riskW = 0;
      let twoOutRbi: number | null = null;
      Object.values(stats).forEach((s: any) => {
        const b = s.batting;
        pa += b.pa; ab += b.ab; so += b.so; bb += b.bb; sb += b.sb;
        if (b.hbp != null) hbp = (hbp ?? 0) + b.hbp;
        if (b.qab != null) qab = (qab ?? 0) + b.qab;
        if (b.ps != null) ps = (ps ?? 0) + b.ps;
        if (b.kLooking != null) kLooking = (kLooking ?? 0) + b.kLooking;
        if (b.twoOutRbi != null) twoOutRbi = (twoOutRbi ?? 0) + b.twoOutRbi;
        if (b.total_ops_weight > 0) { sumOps += b.sum_ops; wOps += b.total_ops_weight; }
        if (b.total_avg_weight > 0) { sumAvg += b.sum_avg; wAvg += b.total_avg_weight; }
        if (b.contactPct != null && b.ab > 0) { contactSum += b.contactPct * b.ab; contactW += b.ab; }
        if (b.baRisp != null && b.ab > 0) { riskSum += b.baRisp * b.ab; riskW += b.ab; }
        const p = s.pitching;
        bbAllowed += p.bb;
        if (p.hbp != null) hbpAllowed += p.hbp;
        sbAllowed += (p.sbAllowed ?? 0);
        csAllowed += (p.csAllowed ?? 0);
      });
      // A team that never allowed a steal is indistinguishable from an import
      // that never carried the column, so treat any caught-stealing attempt as
      // evidence the data is present too.
      const hasSbAgainst = sbAllowed > 0 || csAllowed > 0;
      return {
        pa, ab, so, bb, sb, hbp, qab, ps, kLooking, twoOutRbi,
        qabPct: qab != null && pa > 0 ? (qab as number) / pa : null,
        psPa: ps != null && pa > 0 ? (ps as number) / pa : null,
        kLookingShare: kLooking != null && so > 0 ? (kLooking as number) / so : null,
        contactPct: contactW > 0 ? contactSum / contactW : null,
        baRisp: riskW > 0 ? riskSum / riskW : null,
        avg: wAvg > 0 ? sumAvg / wAvg : null,
        ops: wOps > 0 ? sumOps / wOps : null,
        // Walks and hit batters only, on both sides. The tile is named for
        // exactly that, so folding steals in would contradict its own title.
        //
        // History worth keeping: steals were once added to "taken" with nothing
        // opposite them, because a code comment claimed GC exports carried no
        // steals-against column. They do (pitching SB / CS, column CA), and it
        // is now captured as sbAllowed / csAllowed. It belongs in a running-game
        // read of its own rather than on a walks chart.
        freeBasesTaken: bb + (hbp ?? 0),
        freeBasesGiven: bbAllowed + hbpAllowed,
        // Exposed individually so the tile can show both sides broken out rather
        // than two bare totals a coach has to take on trust.
        bbAllowed,
        hbpAllowed,
        sbAllowed,
        csAllowed,
        hasSbAgainst,
      };
    })();

    const winningFormula = computeWinningFormula(games);
    const hitterTruth = computeHitterTruth(stats);
    const selfInflicted = selfInflictedRuns(Object.values(stats).map((s: any) => s.pitching));

    const hasGameStats = games.some(g => g.stats && Object.keys(g.stats).length > 0);
    const hasAtBatsHint = games.some(g => g.importedFrom === 'scorebook' || g.gcImport?.scorebook);
    const snapshotImportedAt: string | null = isHistoryMode ? null : players.reduce((latest: string | null, p: any) => {
      const at = p?.seasonSnapshot?.importedAt;
      return at && (!latest || at > latest) ? at : latest;
    }, null);

    return {
      loading, games, sortedGames, stats, lastGCOuting, totalGamesWithLogs,
      teamPitching: { teamERA, teamFIP, teamBABIP, teamDSSResult, tIP },
      streakSignals, signalsByPlayer, record, teamAgg: team_,
      winningFormula, hitterTruth, selfInflicted,
      hasGameStats, hasAtBatsHint, snapshotImportedAt, isHistoryMode,
    };
  }, [games, players, isHistoryMode, loading, team?.settings?.fieldTimeSource, team?.defaultInnings]);
}

export type SeasonData = ReturnType<typeof useSeasonStats>;
